import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { kbAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Eye, Calendar } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

export default function Article() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [id]);

  const loadArticle = async () => {
    try {
      const response = await kbAPI.getArticle(id);
      setArticle(response.data);
    } catch (error) {
      toast.error('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        </div>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <h3 className="text-lg font-semibold mb-2">Article not found</h3>
            <Link to="/knowledge-base">
              <Button>Back to Knowledge Base</Button>
            </Link>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        <Link to="/knowledge-base">
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Knowledge Base
          </Button>
        </Link>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between mb-2">
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                {article.category}
              </span>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center">
                  <Eye className="w-4 h-4 mr-1" />
                  {article.views} views
                </span>
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {formatDate(article.created_at)}
                </span>
              </div>
            </div>
            <CardTitle className="text-3xl mb-4">{article.title}</CardTitle>
            <div className="flex flex-wrap gap-2">
              {article.keywords.map((keyword, idx) => (
                <span key={idx} className="bg-muted text-muted-foreground px-2 py-1 rounded text-xs">
                  {keyword}
                </span>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap leading-relaxed">
                {article.content}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-2">Need more help?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              If you couldn't find the answer you're looking for, our support team is here to help.
            </p>
            <Link to="/support">
              <Button>Contact Support</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}